'use client';

import React, { useEffect } from 'react';

import { Modal } from '@depop/web-ui-kit/Modal';
import { useTranslations } from 'next-intl';

import { DownloadTheAppBadgeDesktop } from '../DownloadTheAppBadgeDesktop';
import { DownloadTheAppBadgeMobile } from '../DownloadTheAppBadgeMobile';
import { OfferModalHeader } from '../OfferModalHeader/OfferModalHeader';

import styles from './styles.module.css';

import { MakeOfferModalForm } from '@/components/MakeOfferModal/MakeOfferModalForm';
import { MakeOfferModalSent } from '@/components/MakeOfferModal/MakeOfferModalSent';
import { getCurrencySymbolFromCurrencyCode } from '@/modules/currencies/helpers';
import { ExperimentWrapper } from '@/modules/experiments/ExperimentWrapper';
import { useOffersTracking } from '@/modules/offers/buyers/hooks/useOffersTracking';
import { type OfferProductPrice } from '@/modules/offers/buyers/types';

type Props = {
  isAuthenticated: boolean;
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  productId: number;
  onOfferSent: () => void;
  showOfferSentContent?: boolean;
  productImageHref?: string;
  productPrice: OfferProductPrice;
  selectedVariantId?: number;
  sizeName?: string;
  predefinedOfferPrice?: string;
};

export function MakeOfferModal({
  isAuthenticated,
  isOpen,
  setOpen,
  productId,
  showOfferSentContent,
  onOfferSent,
  productImageHref,
  productPrice,
  selectedVariantId,
  sizeName,
  predefinedOfferPrice,
}: Props) {
  const t = useTranslations('offers');

  const { sendOffersSentViewEvent, sendMakeOfferViewEvent } =
    useOffersTracking();

  const title = showOfferSentContent ? t('OfferSent') : t('MakeOffer');

  const currencySymbol = getCurrencySymbolFromCurrencyCode(
    productPrice.currencyName
  );

  function handleOfferSentSuccess() {
    sendOffersSentViewEvent(productId);
    onOfferSent();
  }

  useEffect(() => {
    if (isOpen) {
      sendMakeOfferViewEvent(productId);
    }
  }, [isOpen, productId]);

  return (
    <Modal
      isOpen={isOpen}
      setOpen={setOpen}
      hideCloseButton
      shouldCloseOnOutsideClick={false}
      labelledBy="make-offer-modal-header"
      customOverrides={{
        floatingHeader: true,
      }}
      content={() => (
        <div className={styles.container}>
          <OfferModalHeader
            title={title}
            onClose={() => setOpen(false)}
            id="make-offer-modal-header"
            displayBanner={!showOfferSentContent}
          />
          {showOfferSentContent ? (
            <>
              <MakeOfferModalSent />
              <ExperimentWrapper experiment="web_8808_make_offer_cta_app_download">
                <div className={styles.desktopOnly}>
                  <DownloadTheAppBadgeDesktop />
                </div>
                <div className={styles.mobileOnly}>
                  <DownloadTheAppBadgeMobile />
                </div>
              </ExperimentWrapper>
            </>
          ) : (
            <MakeOfferModalForm
              predefinedOfferPrice={predefinedOfferPrice}
              currencySymbol={currencySymbol}
              isAuthenticated={isAuthenticated}
              productId={productId}
              productImageHref={productImageHref}
              productPrice={productPrice}
              sizeName={sizeName}
              onOfferSent={handleOfferSentSuccess}
              selectedVariantId={selectedVariantId}
            />
          )}
        </div>
      )}
    />
  );
}
